// ============================================
// @narbodica/auth — onboarding/OnboardingShell.jsx
// ============================================
// Universal onboarding stepper. Handles: progress
// display, forward/back/skip navigation, Narbodica
// Sync detection, and the completion screen.
//
// App-specific steps are passed as the `steps` prop.
//
// SCOPE NOTE (clarified 2 May 2026):
// This component is *designed* to be reusable across
// Narbodica apps — the API takes app name, label,
// accent colour, and steps as props. CURRENT usage
// is Spark-only; this file does not exist in the
// other 9 apps' narb-auth trees. If a future app
// (Connect, Marketplace, etc.) wants this onboarding
// pattern, propagate this file + NarbodicaSync.jsx
// to that app's narb-auth tree and write app-specific
// steps in src/narb-auth/src/onboarding/steps/.
//
// Companion files in this folder:
//   - NarbodicaSync.jsx     — sync detection step
//   - steps/SparkSteps.jsx  — Spark-specific steps definition
//
// Usage:
//   import OnboardingShell from '../narb-auth/src/onboarding/OnboardingShell.jsx';
//   import { getSparkOnboardingSteps } from '../narb-auth/src/onboarding/steps/SparkSteps.jsx';
//
//   <OnboardingShell
//     app="spark"
//     appLabel="Spark"
//     accentColor="#8b5cf6"
//     token={token}
//     user={user}
//     apiBase={API_BASE}             // From src/apiConfig.js (required prop)
//     onComplete={() => navigate('/discover')}
//     steps={getSparkOnboardingSteps()}
//   />
// ============================================

import { useState, useEffect, useCallback } from 'react';
import NarbodicaSync from './NarbodicaSync.jsx';

const API = (base, path) => `${base}/api${path}`;

// ─── Styles ─────────────────────────────────────────────────────────────────
const S = {
  shell: {
    minHeight: '100dvh', background: '#09090b',
    fontFamily: "'Inter', sans-serif",
    display: 'flex', flexDirection: 'column',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '20px 24px', borderBottom: '1px solid #18181b',
    flexShrink: 0,
  },
  logo: (accent) => ({
    fontFamily: "var(--font-serif)",
    fontSize: 22, color: accent, margin: 0,
  }),
  stepLabel: {
    color: '#52525b', fontSize: 13,
  },
  skipBtn: {
    background: 'none', border: 'none', color: '#52525b',
    fontSize: 13, cursor: 'pointer', padding: '4px 8px',
    fontFamily: "'Inter', sans-serif",
    transition: 'color 0.2s',
  },
  progress: (accent) => ({
    height: 2, background: '#18181b', flexShrink: 0,
  }),
  progressBar: (pct, accent) => ({
    height: '100%', background: accent,
    width: `${pct}%`, transition: 'width 0.4s ease',
  }),
  body: {
    flex: 1, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    padding: '32px 24px',
  },
  inner: {
    width: '100%', maxWidth: 480,
    display: 'flex', flexDirection: 'column', gap: 24,
  },
  stepTitle: {
    color: '#fafafa', fontSize: 26, fontWeight: 700,
    fontFamily: "var(--font-serif)", margin: 0,
  },
  stepSubtitle: {
    color: '#71717a', fontSize: 14, margin: 0, lineHeight: 1.6,
  },
  footer: {
    display: 'flex', gap: 12, padding: '20px 24px',
    borderTop: '1px solid #18181b', flexShrink: 0,
  },
  btnBack: {
    flex: '0 0 auto', padding: '13px 24px',
    background: 'none', border: '1px solid #27272a',
    borderRadius: 12, color: '#a1a1aa',
    fontSize: 14, fontWeight: 600, cursor: 'pointer',
    fontFamily: "'Inter', sans-serif",
  },
  btnNext: (accent, disabled) => ({
    flex: 1, padding: '13px 0',
    background: disabled ? '#27272a' : accent,
    border: 'none', borderRadius: 12,
    color: disabled ? '#52525b' : '#fff',
    fontSize: 14, fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: "'Inter', sans-serif",
    transition: 'background 0.2s',
  }),
  dots: {
    display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'center',
  },
  dot: (active, accent) => ({
    width: active ? 20 : 6, height: 6, borderRadius: 3,
    background: active ? accent : '#27272a',
    transition: 'all 0.3s',
  }),
  completionWrap: {
    flex: 1, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    padding: '40px 24px', gap: 24, textAlign: 'center',
  },
  completionIcon: {
    width: 72, height: 72, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 32,
  },
  completionTitle: {
    color: '#fafafa', fontSize: 28,
    fontFamily: "var(--font-serif)", margin: 0,
  },
  completionSub: {
    color: '#71717a', fontSize: 14, margin: 0, lineHeight: 1.7,
    maxWidth: 320,
  },
  completionBtn: (accent) => ({
    padding: '14px 48px', background: accent,
    border: 'none', borderRadius: 12,
    color: '#fff', fontSize: 15, fontWeight: 600,
    cursor: 'pointer', fontFamily: "'Inter', sans-serif",
  }),
};

// ─── Step context — shared between shell and step components ─────────────────
// Each step component receives: { onNext, onBack, onSkip, canProceed, setCanProceed, data, setData }

// ─── Completion screen ───────────────────────────────────────────────────────
function CompletionScreen({ appLabel, accentColor, onComplete }) {
  return (
    <div style={S.completionWrap}>
      <div style={{ ...S.completionIcon, background: `${accentColor}20` }}>✨</div>
      <h2 style={S.completionTitle}>You're all set</h2>
      <p style={S.completionSub}>
        {appLabel} is ready to go. Everything you've shared helps us make it more useful for you.
        You can update any of this later in your settings.
      </p>
      <button onClick={onComplete} style={S.completionBtn(accentColor)}>
        Let's go
      </button>
    </div>
  );
}

// ─── Main Shell ───────────────────────────────────────────────────────────────
export default function OnboardingShell({
  app,
  appLabel,
  accentColor = '#8b5cf6',
  token,
  user,
  apiBase,
  onComplete,
  steps = [],       // [{ id, label, title, subtitle, required, component }]
  showSync = true,  // Whether to show the Narbodica Sync step
}) {
  // Updated 2 May 2026 (Cassiopeia stretch 13) — apiBase is now a required
  // prop. Same pattern as NarbodicaAuth and passkey.js. Removed the
  // hardcoded URL fallback to maintain single source of truth.
  if (!apiBase) {
    throw new Error('OnboardingShell: apiBase prop is required. Pass it from your app\'s utils/apiBase.js or apiConfig.js.');
  }

  const [phase, setPhase] = useState('checking'); // checking | sync | steps | complete
  const [syncData, setSyncData] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [canProceed, setCanProceed] = useState(true);
  const [stepData, setStepData] = useState({});   // { stepId: data }
  const [skippedSteps, setSkippedSteps] = useState(new Set());

  // ── Check for cross-app imports on mount ──────────────────────────────────
  useEffect(() => {
    if (!showSync || !token) { setPhase('steps'); return; }

    fetch(API(apiBase, '/api/cross-app/merged-imports'), {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(data => {
        if (data.success && data.data?.hasImports && data.data?.fields?.length > 0) {
          setSyncData(data.data);
          setPhase('sync');
        } else {
          setPhase('steps');
        }
      })
      .catch(() => setPhase('steps'));
  }, [token, showSync]);

  // ── Navigation ────────────────────────────────────────────────────────────
  const goNext = useCallback(() => {
    setCanProceed(true); // reset for next step
    if (currentStep < steps.length - 1) {
      setCurrentStep(s => s + 1);
    } else {
      setPhase('complete');
    }
  }, [currentStep, steps.length]);

  const goBack = useCallback(() => {
    setCanProceed(true);
    if (currentStep > 0) {
      setCurrentStep(s => s - 1);
    }
  }, [currentStep]);

  const skipStep = useCallback(() => {
    setSkippedSteps(prev => new Set([...prev, steps[currentStep]?.id]));
    setCanProceed(true);
    goNext();
  }, [currentStep, steps, goNext]);

  const updateStepData = useCallback((stepId, data) => {
    setStepData(prev => ({ ...prev, [stepId]: data }));
  }, []);

  // ── Progress ──────────────────────────────────────────────────────────────
  const totalSteps = steps.length;
  const progressPct = totalSteps > 0 ? ((currentStep + 1) / totalSteps) * 100 : 100;
  const currentStepDef = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;

  // ─── PHASE: checking ─────────────────────────────────────────────────────
  if (phase === 'checking') {
    return (
      <div style={{ ...S.shell, alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 28, height: 28, border: `2px solid ${accentColor}40`, borderTopColor: accentColor, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      </div>
    );
  }

  // ─── PHASE: sync ─────────────────────────────────────────────────────────
  if (phase === 'sync') {
    return (
      <div style={S.shell}>
        <div style={S.header}>
          <h1 style={S.logo(accentColor)}>{appLabel}</h1>
          <span style={S.stepLabel}>Narbodica Sync</span>
        </div>
        <NarbodicaSync
          app={app}
          appLabel={appLabel}
          accentColor={accentColor}
          token={token}
          apiBase={apiBase}
          syncData={syncData}
          onAccept={() => setPhase('steps')}
          onDecline={() => setPhase('steps')}
        />
      </div>
    );
  }

  // ─── PHASE: complete ──────────────────────────────────────────────────────
  if (phase === 'complete') {
    return (
      <div style={S.shell}>
        <div style={S.header}>
          <h1 style={S.logo(accentColor)}>{appLabel}</h1>
        </div>
        <div style={{ ...S.progress(accentColor) }}>
          <div style={S.progressBar(100, accentColor)} />
        </div>
        <CompletionScreen appLabel={appLabel} accentColor={accentColor} onComplete={onComplete} />
      </div>
    );
  }

  // ─── PHASE: steps ────────────────────────────────────────────────────────
  if (!currentStepDef) {
    // No steps defined — go straight to complete
    return <CompletionScreen appLabel={appLabel} accentColor={accentColor} onComplete={onComplete} />;
  }

  const stepProps = {
    onNext: goNext,
    onBack: goBack,
    onSkip: skipStep,
    canProceed,
    setCanProceed,
    data: stepData[currentStepDef.id] || {},
    setData: (d) => updateStepData(currentStepDef.id, d),
    token,
    user,
    apiBase,
    accentColor,
    allStepData: stepData,
  };

  return (
    <div style={S.shell}>
      {/* Header */}
      <div style={S.header}>
        <h1 style={S.logo(accentColor)}>{appLabel}</h1>
        <span style={S.stepLabel}>{currentStep + 1} of {totalSteps}</span>
        {!currentStepDef.required && (
          <button style={S.skipBtn} onClick={skipStep}
            onMouseEnter={e => e.target.style.color = '#a1a1aa'}
            onMouseLeave={e => e.target.style.color = '#52525b'}>
            Skip
          </button>
        )}
      </div>

      {/* Progress bar */}
      <div style={S.progress(accentColor)}>
        <div style={S.progressBar(progressPct, accentColor)} />
      </div>

      {/* Step body */}
      <div style={S.body}>
        <div style={S.inner}>
          {currentStepDef.title && (
            <div>
              <h2 style={S.stepTitle}>{currentStepDef.title}</h2>
              {currentStepDef.subtitle && <p style={S.stepSubtitle}>{currentStepDef.subtitle}</p>}
            </div>
          )}

          {/* Render step component with props injected */}
          {typeof currentStepDef.component === 'function'
            ? currentStepDef.component(stepProps)
            : currentStepDef.component
              ? { ...currentStepDef.component, props: { ...currentStepDef.component.props, ...stepProps } }
              : null}
        </div>
      </div>

      {/* Dot indicators */}
      {totalSteps > 1 && (
        <div style={S.dots}>
          {steps.map((_, i) => (
            <div key={i} style={S.dot(i === currentStep, accentColor)} />
          ))}
        </div>
      )}

      {/* Footer navigation */}
      <div style={S.footer}>
        {currentStep > 0 && (
          <button style={S.btnBack} onClick={goBack}>← Back</button>
        )}
        <button
          style={S.btnNext(accentColor, !canProceed)}
          onClick={goNext}
          disabled={!canProceed}
        >
          {isLastStep ? 'Finish' : 'Continue →'}
        </button>
      </div>
    </div>
  );
}
